import { ui } from "@/components/ui";

import { useDeleteTemplate } from "@/features/templates/api/deleteTemplate";
import { useUpdateTemplate } from "@/features/templates/api/updateTemplate";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { useRouter } from "@tanstack/react-router";
import {
  ClipboardEdit,
  Edit,
  MoreVertical,
  Save,
  Share2,
  Trash,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import type { Template, TemplateT } from "../../../types/database/quickstarts";

type TemplateWithAccess = TemplateT & {
  templates_access: {
    access: "viewer" | "editor";
  }[];
};

const quickstartId = uuidv4();

function TemplateControls({ template }: { template: Template }) {
  const router = useRouter();

  const templateSignupUrl = window.location.origin +
    router.buildLocation({
      to: "/signup",
      search: {
        ...template.settings,
        role: "PATIENT",
        organisation: {
          organisation_id: template.settings.organisation_id,
        },
        quickstart_key: template?.quickstart_key?.toUpperCase() ?? "INJECTION",
        quickstart_id: quickstartId,
      },
    }).href;

  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(template.template_name || "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const { mutate: updateTemplate } = useUpdateTemplate();
  const store = useTemplateStore();
  const settings = template.settings;
  const isEditor = template.templates_access?.[0]?.access === "editor";

  const handleEditTemplate = () => {
    if (template.settings) {
      store.setState(template.settings);
    }
    router.navigate({
      to: "/new/diary/$templateId/organisation",
      params: { templateId: template.template_id },
    });
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleConfirmDelete = () => {
    deleteTemplate(template.template_id);
    setDeleteDialogOpen(false);
  };
  const handleEditStart = () => setIsEditing(true);
  const handleSave = () => {
    updateTemplate({
      template_id: template.template_id,
      formData: {
        template_name: newTitle,
        template_description: template.template_description,
      } as Template["Update"],
    });
    setIsEditing(false);
    toast.success("Template updated successfully");
  };
  const handleCopy = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    navigator.clipboard.writeText(templateSignupUrl);
    toast.success("Copied to clipboard");
  };

  return (
    <>
      {isEditing
        ? (
          <div className="flex-1">
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="bg-transparent px-0 py-0 border-0 border-slate-600 border-b focus:outline-none focus:ring-0 font-bold text-xl"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                }
              }}
            />
          </div>
        )
        : (
          <h3 className="font-bold text-xl line-clamp-1">
            {template.template_name || "Unnamed Template"}
          </h3>
        )}
      {isEditor && (
        <ui.Popover>
          <ui.PopoverTrigger asChild>
            <ui.Button variant="ghost" size="icon">
              <MoreVertical size={20} />
            </ui.Button>
          </ui.PopoverTrigger>
          <ui.PopoverContent align="end" className="p-1 w-40">
            <div className="flex flex-col">
              {isEditing
                ? (
                  <ui.Button
                    variant="ghost"
                    size="sm"
                    className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto text-indigo-600 hover:text-indigo-700"
                    onClick={handleSave}
                  >
                    <Save size={20} />
                    Save
                  </ui.Button>
                )
                : (
                  <ui.Button
                    variant="ghost"
                    size="sm"
                    className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                    onClick={handleEditStart}
                  >
                    <Edit size={20} />
                    Rename
                  </ui.Button>
                )}
              <ui.Button
                variant="ghost"
                size="sm"
                className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                onClick={handleEditTemplate}
              >
                <ClipboardEdit size={20} />
                Edit Template
              </ui.Button>
              <ui.Button
                variant="ghost"
                size="sm"
                className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                onClick={handleCopy}
              >
                <Share2 size={20} />
                Copy URL
              </ui.Button>
              <ui.Button
                variant="ghost"
                size="sm"
                className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto text-red-600 hover:text-red-600"
                onClick={handleDeleteClick}
              >
                <Trash size={20} />
                Delete
              </ui.Button>
            </div>
          </ui.PopoverContent>
        </ui.Popover>
      )}

      <ui.Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <ui.DialogContent>
          <ui.DialogHeader>
            <ui.DialogTitle>Delete Template</ui.DialogTitle>
            <ui.DialogDescription>
              Are you sure you want to delete this template? This action cannot
              be undone.
            </ui.DialogDescription>
          </ui.DialogHeader>
          <ui.DialogFooter>
            <ui.Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </ui.Button>
            <ui.Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </ui.Button>
          </ui.DialogFooter>
        </ui.DialogContent>
      </ui.Dialog>
    </>
  );
}

export default TemplateControls;
